// GET /api/meta: product name, tagline, help address, legal links, which SSO
// buttons to show. Loaded once per visit; public (works signed out).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Meta } from "@convenor/shared/v3";
import { brand, setBrand } from "../lib/brand";
import { api } from "./client";

export const metaKey = ["meta"] as const;

export function defaultMeta(): Meta {
  const b = brand();
  return {
    appName: b.appName,
    tagline: b.tagline,
    supportEmail: b.supportEmail,
    privacyUrl: "/privacy",
    termsUrl: "/terms",
    accessibilityUrl: "/accessibility",
    termsVersion: "",
    languages: [{ code: "en", label: "English" }],
    sso: { microsoft: false, google: false },
    importConnectors: [],
    signupAllowed: true,
    turnstileSiteKey: null,
    demoMode: false,
    demoTrialUrl: null,
    demoFormUrl: null,
    demoStartTrial: false,
  };
}

function useMetaQuery() {
  return useQuery({
    queryKey: metaKey,
    queryFn: async () => {
      const m = await api.get<Meta>("/api/meta");
      setBrand({ appName: m.appName, supportEmail: m.supportEmail, tagline: m.tagline });
      return m;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The server's Meta, or the defaults while it loads (never undefined). */
export function useMeta(): Meta {
  return useMetaQuery().data ?? defaultMeta();
}

/**
 * Whether new accounts may be created here; undefined while /api/meta loads, so a page
 * shows neither the sign-up form nor the "closed" message until it knows. When /api/meta
 * cannot be reached, the default (allowed) applies and the server still decides.
 */
export function useSignupAllowed(): boolean | undefined {
  const q = useMetaQuery();
  if (q.data) return q.data.signupAllowed;
  return q.isError ? defaultMeta().signupAllowed : undefined;
}

/**
 * Whether this is the public demo; undefined while /api/meta loads, so a sign-in page
 * neither flashes its forms nor the demo entry before it knows. When /api/meta cannot be
 * reached, it is not the demo.
 */
export function useDemoMode(): boolean | undefined {
  const q = useMetaQuery();
  if (q.data) return q.data.demoMode;
  return q.isError ? false : undefined;
}

/**
 * The bot check's site key: null = no bot check; undefined while /api/meta loads,
 * so a form neither flashes the widget nor lets a submit through before it knows.
 */
export function useTurnstileSiteKey(): string | null | undefined {
  const q = useMetaQuery();
  if (q.data) return q.data.turnstileSiteKey ?? null;
  return q.isError ? defaultMeta().turnstileSiteKey : undefined;
}

/**
 * Loads /api/meta again, past the browser's cache (it is cached for 5 minutes), and
 * updates every page that reads it. For when the server says the page's copy is out
 * of date, e.g. a sign-up refused by a bot check the page did not know about.
 */
export function useRefreshMeta(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    try {
      const res = await fetch("/api/meta", { cache: "no-cache", credentials: "include", headers: { Accept: "application/json" } });
      if (res.ok) queryClient.setQueryData(metaKey, (await res.json()) as Meta);
    } catch {
      // Offline: the form's message still tells the person what to do.
    }
  };
}
